import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { Box } from '@mui/material';
import { useSubscriptionsPlansData } from 'constants/subscriptions';
import { SubscriptionPlansLayout } from 'layouts/SubscriptionPlansLayout';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getIsSubscriptionLoading, getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { useToggle } from 'hooks/useToggler';

import { SUBSCRIPTION_RECURRENT } from 'interfaces/subscriptions';

import { Loader } from 'components/_basic/Loader/Loader';
import { useAuthModalsContext } from 'components/AuthModals';
import { CancelSubscriptionModal } from 'components/CancelSubscriptionModal/CancelSubscriptionModal';
import { PlanCard } from 'components/PlanCard/PlanCard';
import { CustomizedSwitches } from 'components/Switch/Switch';

import { formatDate } from 'helpers/formatDates';
import ToastService from 'helpers/ToastService';

import styles from '../SubscriptionsPlansPage/SubscriptionsPlansPage.module.scss';

export const OnlyPayedSubscriptionsPlansPage = () => {
  const [isYearlySubscription, setIsYearlySubscription] = useState(false);
  const [isCancelModalOpened, setIsCancelModalOpened] = useToggle(false);

  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isSubscriptionLoading = useUserSubscriptionStore(getIsSubscriptionLoading);

  const allSubscriptionsPlans = useSubscriptionsPlansData();
  const payedSubscriptions = allSubscriptionsPlans.slice(1);

  const handleSwitchSubscription = (value: boolean) => {
    setIsYearlySubscription(value);
  };

  const handleCancelSubscription = () => {
    setIsCancelModalOpened();
  };
  const { setIsModalOpened } = useAuthModalsContext();

  useEffect(() => {
    setIsModalOpened(false);
  }, []);

  // Fix showing toast after stripe payment
  useEffect(() => {
    if (userSubscription?.nextSubscription.recurrent === SUBSCRIPTION_RECURRENT.MONTHLY) {
      ToastService.showSuccess(
        `Your "${userSubscription.nextSubscription.subscriptionPlan}" monthly subscription starts on ${formatDate(userSubscription.currentSubscription.endDate)}`,
        `Your "${userSubscription.currentSubscription.subscriptionPlan}" annual subscription is cancelled`,
        {
          autoClose: 3000,
          className: styles.toast,
        },
      );
    }
  }, [userSubscription?.nextSubscription?.recurrent]);

  if (isSubscriptionLoading) {
    return <Loader />;
  }

  return (
    <>
      <Helmet>
        <script type="text/javascript" defer>
          {`
            window.dataLayer = window.dataLayer || [];
            let userId = '';
            try {
              const userStore = localStorage.getItem('user-store');
              if (userStore) {
                const parsed = JSON.parse(userStore);
                userId = parsed?.state?.authUser?.id || '';
                dataLayer.push({ user_id: userId });
              }
            } catch (e) {
              console.error('Failed to parse user-store:', e);
            }
          `}
        </script>
      </Helmet>
      <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
        <Box marginBottom={{ xs: '24px', md: '32px' }}>
          <CustomizedSwitches switcherValue={isYearlySubscription} onChange={handleSwitchSubscription} />
        </Box>
        <SubscriptionPlansLayout>
          {payedSubscriptions.map((subscriptionsPlan) => (
            <PlanCard
              key={subscriptionsPlan.tariff}
              isYearly={isYearlySubscription}
              subscriptionData={subscriptionsPlan}
              onCancelSubscription={handleCancelSubscription}
            />
          ))}
        </SubscriptionPlansLayout>
        {userSubscription && (
          <CancelSubscriptionModal
            isOpened={isCancelModalOpened}
            handleClose={setIsCancelModalOpened}
            userSubscription={userSubscription}
          />
        )}
      </Box>
    </>
  );
};
